import { useMemo, useState } from 'react';
import { Combobox, Option, type ComboboxProps } from '@fluentui/react-components';

export interface ComboOption {
  value: string;
  label: string;
}

/** Numbers sort as numbers ("2" before "10"), text alphabetically, case and accents ignored. */
export function compareLabels(a: string, b: string): number {
  return a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' });
}

/** The row a dropdown lists first — what a "default to the first value" rule must pick. */
export function firstInOrder<T>(rows: readonly T[], label: (row: T) => string): T | undefined {
  return rows.toSorted((a, b) => compareLabels(label(a), label(b)))[0];
}

/** Every word typed must appear somewhere in the label, in any order, case-insensitively. */
export function matchesQuery(label: string, query: string): boolean {
  const haystack = label.toLowerCase();
  return query
    .toLowerCase()
    .split(/\s+/)
    .filter(Boolean)
    .every((word) => haystack.includes(word));
}

type PassThrough = Omit<
  ComboboxProps,
  | 'children'
  | 'value'
  | 'selectedOptions'
  | 'onOptionSelect'
  | 'onChange'
  | 'onSelect'
  | 'multiselect'
>;

export interface FilterableComboboxProps extends PassThrough {
  options: readonly ComboOption[];
  selectedValue: string | null;
  /** What to show for a selected value that is not (yet) among the options, e.g. while loading. */
  fallbackLabel?: string;
  /** `null` when the selection is cleared. */
  onSelect: (value: string | null) => void;
}

/**
 * The one dropdown behaviour for every D365-backed list in the app: options always in natural
 * order (numbers numerically, text alphabetically), and typing narrows the list to the labels
 * that contain what was typed — wherever in the label it appears — while keeping that order.
 * While the user types the input shows what they typed; choosing an option or leaving the field
 * returns it to the selected label.
 */
export function FilterableCombobox({
  options,
  selectedValue,
  fallbackLabel,
  onSelect,
  onBlur,
  onFocus,
  ...rest
}: FilterableComboboxProps) {
  // null = not typing: the input mirrors the current selection.
  const [query, setQuery] = useState<string | null>(null);
  const sorted = useMemo(
    () => options.toSorted((a, b) => compareLabels(a.label, b.label)),
    [options],
  );
  const selected = sorted.find((option) => option.value === selectedValue);
  const typed = (query ?? '').trim();
  const visible = typed ? sorted.filter((option) => matchesQuery(option.label, typed)) : sorted;

  return (
    <Combobox
      {...rest}
      selectedOptions={selected ? [selected.value] : []}
      value={query ?? selected?.label ?? fallbackLabel ?? selectedValue ?? ''}
      onChange={(event) => {
        const raw = event.target.value;
        // The box shows the selected label, so a keystroke appends to it ("1001 — Operations2").
        // Whatever follows the label is what the user typed: search on that, not on the lot.
        const shown = selected?.label ?? fallbackLabel ?? '';
        const typedAfterLabel =
          query === null && shown !== '' && raw.length > shown.length && raw.startsWith(shown);
        setQuery(typedAfterLabel ? raw.slice(shown.length) : raw);
      }}
      onFocus={(event) => {
        // Select what is shown so the first keystroke replaces it rather than extending it.
        event.currentTarget.select();
        onFocus?.(event);
      }}
      onOptionSelect={(event, data) => {
        // Typing makes Fluent drop the current selection and report it as an "option select" with
        // no value, from the input's change event. That is not the user choosing to clear: ignore
        // it, keep what was typed, and let blur put the selected label back. The clear (✕)
        // button reports the same thing from a click, which is the one real way to clear.
        if (data.optionValue === undefined && event.type !== 'click') return;
        setQuery(null);
        // `|| null`: an empty option value must never be stored as a selection.
        onSelect(data.optionValue || null);
      }}
      onBlur={(event) => {
        setQuery(null);
        onBlur?.(event);
      }}
    >
      {visible.map((option) => (
        <Option key={option.value} value={option.value} text={option.label}>
          {option.label}
        </Option>
      ))}
      {typed && visible.length === 0 && (
        <Option key="__no-match" value="" text="" disabled>
          No matches
        </Option>
      )}
    </Combobox>
  );
}
